import { z } from 'zod';

export const StateMachineKeySchema = z.enum([
  'project',
  'project_thread',
  'project_change',
  'project_change_revision',
  'task',
  'workflow_run',
  'gate',
  'blocker',
  'handoff',
  'conversation_interaction',
  'action_queue_item',
  'improvement_proposal',
]);
export type StateMachineKey = z.infer<typeof StateMachineKeySchema>;

export const ActorRefSchema = z
  .object({
    type: z.enum(['human_user', 'agent', 'daemon', 'system', 'worker', 'api', 'service_account']),
    id: z.string().min(1).nullable().optional(),
  })
  .strict();
export type ActorRef = z.infer<typeof ActorRefSchema>;

export const EntityRefSchema = z
  .object({
    type: z.string().min(1),
    id: z.string().min(1),
    versionHash: z.string().nullable().optional(),
  })
  .strict();
export type EntityRef = z.infer<typeof EntityRefSchema>;

export const StateRefSchema = z
  .object({
    entityType: z.enum([
      'state_event',
      'gate',
      'blocker',
      'handoff',
      'task',
      'project',
      'project_thread',
      'project_change',
      'project_change_revision',
      'workflow_run',
      'conversation_interaction',
      'action_queue_item',
    ]),
    entityId: z.union([z.string().min(1), z.number().int().positive()]),
    projectId: z.number().int().positive().optional(),
    statusField: z.string().min(1).optional(),
  })
  .strict();
export type StateRef = z.infer<typeof StateRefSchema>;

export interface StateTransitionDefinition {
  machine: StateMachineKey;
  from: string;
  event: string;
  to: string;
}

type TransitionMap = Record<string, readonly string[]>;

export const STATE_TRANSITIONS = {
  project: {
    active: ['on_hold', 'archived', 'closed'],
    on_hold: ['active', 'archived', 'closed'],
    archived: ['active', 'closed'],
    closed: [],
  },
  project_thread: {
    active: ['paused', 'completed', 'archived'],
    paused: ['active', 'completed', 'archived'],
    completed: ['archived'],
    archived: [],
  },
  project_change: {
    draft: ['planned', 'implementing', 'blocked', 'abandoned'],
    planned: ['implementing', 'blocked', 'abandoned'],
    implementing: ['checking', 'reviewing', 'blocked', 'abandoned'],
    checking: ['implementing', 'reviewing', 'blocked', 'abandoned'],
    reviewing: ['implementing', 'ready_to_merge', 'blocked', 'abandoned'],
    ready_to_merge: ['reviewing', 'merged', 'blocked', 'abandoned'],
    blocked: ['planned', 'implementing', 'checking', 'reviewing', 'ready_to_merge', 'abandoned'],
    merged: [],
    abandoned: [],
  },
  project_change_revision: {
    draft: ['generated', 'failed', 'superseded'],
    generated: ['validating', 'superseded'],
    validating: ['validated', 'failed', 'superseded'],
    validated: ['superseded'],
    failed: ['superseded'],
    superseded: [],
  },
  task: {
    pending: ['in_progress', 'failed', 'cancelled'],
    in_progress: ['awaiting_approval', 'awaiting_user', 'completed', 'failed', 'cancelled'],
    awaiting_approval: ['in_progress', 'failed', 'cancelled'],
    awaiting_user: ['in_progress', 'failed', 'cancelled'],
    completed: [],
    failed: [],
    cancelled: [],
  },
  workflow_run: {
    pending: ['running', 'failed', 'cancelled'],
    running: ['waiting_human', 'completed', 'failed', 'cancelled'],
    waiting_human: ['running', 'completed', 'failed', 'cancelled'],
    completed: [],
    failed: [],
    cancelled: [],
  },
  gate: {
    pending: ['approved', 'rejected', 'request_changes', 'expired', 'superseded', 'cancelled'],
    approved: ['expired', 'superseded'],
    rejected: ['superseded'],
    request_changes: ['superseded'],
    expired: ['superseded'],
    superseded: [],
    cancelled: [],
  },
  blocker: {
    open: ['acknowledged', 'assigned', 'waiting', 'resolved', 'dismissed', 'superseded'],
    acknowledged: ['assigned', 'waiting', 'resolved', 'dismissed', 'superseded'],
    assigned: ['waiting', 'resolved', 'dismissed', 'superseded'],
    waiting: ['assigned', 'resolved', 'dismissed', 'superseded'],
    resolved: ['superseded'],
    dismissed: ['superseded'],
    superseded: [],
  },
  handoff: {
    requested: ['accepted', 'in_progress', 'blocked', 'returned', 'failed', 'cancelled', 'expired', 'superseded'],
    accepted: ['in_progress', 'blocked', 'returned', 'failed', 'cancelled', 'expired', 'superseded'],
    in_progress: ['blocked', 'returned', 'completed', 'failed', 'cancelled', 'expired', 'superseded'],
    blocked: ['in_progress', 'returned', 'failed', 'cancelled', 'expired', 'superseded'],
    returned: ['requested', 'cancelled', 'superseded'],
    completed: ['superseded'],
    failed: ['superseded'],
    cancelled: [],
    expired: ['superseded'],
    superseded: [],
  },
  conversation_interaction: {
    open: ['submitted', 'cancelled', 'expired', 'superseded'],
    submitted: ['superseded'],
    cancelled: [],
    expired: ['superseded'],
    superseded: [],
  },
  action_queue_item: {
    open: ['claimed', 'waiting', 'resolved', 'cancelled', 'expired'],
    claimed: ['open', 'waiting', 'resolved', 'cancelled', 'expired'],
    waiting: ['open', 'claimed', 'resolved', 'cancelled', 'expired'],
    resolved: [],
    cancelled: [],
    expired: [],
  },
  // Self-Improvement S1 — the proposal spine lifecycle. Every non-terminal status
  // may be superseded by a descendant. Terminal: measured, rejected, failed,
  // superseded. The `critical` risk-class guard (critical → implementing is ALWAYS
  // illegal) is enforced by `canTransitionProposal` / the engine transition service,
  // not the generic machine below (see 03-proposal-spine §7 T0 invariant #2).
  improvement_proposal: {
    idea: ['specced', 'rejected', 'superseded', 'failed'],
    specced: ['implementing', 'rejected', 'superseded', 'failed'],
    implementing: ['auditing', 'failed', 'superseded'],
    auditing: ['ready_for_human', 'implementing', 'failed', 'superseded'],
    ready_for_human: ['approved', 'rejected', 'superseded'],
    approved: ['applying', 'superseded'],
    applying: ['applied', 'failed', 'superseded'],
    applied: ['measured', 'superseded'],
    measured: [],
    rejected: [],
    failed: [],
    superseded: [],
  },
} as const satisfies Record<StateMachineKey, TransitionMap>;

/**
 * Terminal proposal statuses — no outgoing transitions. Used by the transition
 * service to enforce terminal immutability and by the ratchet in
 * `canTransitionProposal`.
 */
export const TERMINAL_PROPOSAL_STATUSES = ['measured', 'rejected', 'failed', 'superseded'] as const;

/**
 * Risk-aware legality check for the improvement-proposal machine.
 *
 * Layered on top of the generic `improvement_proposal` transition map with ONE
 * extra hard rule (T0 invariant #2, cannot be configured off): a `critical`
 * risk-class proposal may NEVER enter `implementing` — the loop may file the idea
 * and evidence, but a human must implement it. This is enforced here at the
 * state-machine layer so every caller (service, tests) shares one source of truth.
 */
export function canTransitionProposal(from: string, to: string, riskClass?: string): boolean {
  if (riskClass === 'critical' && to === 'implementing') return false;
  return canTransitionState('improvement_proposal', from, to);
}

export function canTransitionState(machine: StateMachineKey, from: string, to: string): boolean {
  const machineTransitions = STATE_TRANSITIONS[machine] as unknown as TransitionMap;
  return Boolean(machineTransitions[from]?.includes(to));
}

export function getAllowedTargetStates(machine: StateMachineKey, from: string): readonly string[] {
  const machineTransitions = STATE_TRANSITIONS[machine] as unknown as TransitionMap;
  return machineTransitions[from] ?? [];
}

export function assertStateTransition(machine: StateMachineKey, from: string, to: string): void {
  if (!canTransitionState(machine, from, to)) {
    throw new Error(`Invalid ${machine} transition: ${from} -> ${to}`);
  }
}
